import { useTranslation } from "react-i18next";
import type { OverviewKpiSeries } from "@nexus/contracts";
import type { TooltipContentProps } from "recharts";
import {
  formatRawMetric,
  rawMetricKey,
  type ChartDatum,
  type ResolvedMetric,
} from "./chartModel.js";
import {
  KPI_TONE_CLASS,
  resolveKpiBadge,
  type KpiBadge,
} from "../../lib/topicDetailKpis.js";

/** Slate ink for every axis, tick, and value label. */
export const CHART_AXIS_TEXT = "var(--chart-axis-text)";

/** Very light rule shared by the horizontal grid and the axis lines. */
export const CHART_GRID_STROKE = "var(--chart-grid)";

/** Axis/tick labels are bold so they hold their own against the pastel fills. */
export const CHART_LABEL_FONT_WEIGHT = 700;

export const CHART_TICK_FONT_SIZE = 13;

/** Total horizontal gutter between neighboring category labels (4px each side). */
const LABEL_BAND_GUTTER = 8;

/** Prefer a word break when the cut point sits this many characters past a space. */
const WORD_BOUNDARY_SLACK = 3;

const ELLIPSIS = "…";

/**
 * Must match the SVG tick's rendered font so canvas measurements agree with
 * what the user sees. Mirrors DESIGN.md / design-system.css `--font-sans`.
 */
const AXIS_LABEL_FONT = `${CHART_LABEL_FONT_WEIGHT} ${CHART_TICK_FONT_SIZE}px Inter, -apple-system, system-ui, "Segoe UI", Roboto, sans-serif`;

const textWidthCache = new Map<string, number>();
let measureCanvas: HTMLCanvasElement | null = null;

/** Measured pixel width of `text` in the axis label font. Cached per string. */
export function measureAxisLabelWidth(text: string): number {
  const cached = textWidthCache.get(text);
  if (cached != null) return cached;

  if (typeof document === "undefined") {
    // SSR / test fallback — Inter 13px bold averages ~7.5px per character.
    const approx = text.length * 7.5;
    textWidthCache.set(text, approx);
    return approx;
  }

  if (!measureCanvas) measureCanvas = document.createElement("canvas");
  const ctx = measureCanvas.getContext("2d");
  if (!ctx) {
    const approx = text.length * 7.5;
    textWidthCache.set(text, approx);
    return approx;
  }

  ctx.font = AXIS_LABEL_FONT;
  const width = ctx.measureText(text).width;
  textWidthCache.set(text, width);
  return width;
}

/**
 * Truncate `full` so its rendered width (plus ellipsis when shortened) fits in
 * `maxWidth` px. Keeps the start of the string; prefers a word boundary within
 * WORD_BOUNDARY_SLACK of the cut. Labels that fit exactly are left untouched.
 */
export function truncateLabelToWidth(full: string, maxWidth: number): string {
  if (full.length === 0) return full;
  if (measureAxisLabelWidth(full) <= maxWidth) return full;

  const ellipsisWidth = measureAxisLabelWidth(ELLIPSIS);
  const contentBudget = maxWidth - ellipsisWidth;

  // Too narrow for even "X…" — still emit a single character + ellipsis rather
  // than an empty label (spec floor for very narrow containers).
  if (contentBudget <= 0 || measureAxisLabelWidth(full[0]!) > contentBudget) {
    return `${full[0]}${ELLIPSIS}`;
  }

  let lo = 1;
  let hi = full.length - 1;
  let best = 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (measureAxisLabelWidth(full.slice(0, mid)) <= contentBudget) {
      best = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }

  let cut = best;
  const cutsMidWord =
    cut < full.length && full[cut] !== " " && full[cut - 1] !== " ";
  if (cutsMidWord) {
    const searchFrom = Math.max(1, cut - WORD_BOUNDARY_SLACK);
    const spaceAt = full.lastIndexOf(" ", cut - 1);
    if (spaceAt >= searchFrom) cut = spaceAt;
  }

  while (cut > 1 && full[cut - 1] === " ") cut -= 1;
  cut = Math.max(1, cut);

  return `${full.slice(0, cut)}${ELLIPSIS}`;
}

/** Clears the measurement cache — exported for tests. */
export function __resetAxisLabelWidthCache(): void {
  textWidthCache.clear();
}

/**
 * Format a raw Topic_Title for display (mirrors the Retool overview transformer, bare
 * variant): drop the type prefix up to & INCLUDING the first `;` (only the first — a
 * value like `a;b;c` → `b;c`), turn runs of `_` into spaces, capitalize each word, trim.
 * A value with NO `;` keeps the whole string (prefix-less fallback — do not "fix" this
 * into returning empty). `custom;positive`→"Positive", `main;problem_solution`→"Problem
 * Solution", `user_emotions;empowerment`→"Empowerment".
 */
export function formatTopicTitle(raw: string): string {
  const semi = raw.indexOf(";");
  const body = semi === -1 ? raw : raw.slice(semi + 1);
  return body
    .replace(/_+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

interface AxisTickProps {
  x?: number;
  y?: number;
  payload?: { value?: string | number };
  /** Plot-area width of the X axis — supplied by Recharts on every tick. */
  width?: number;
  /** Number of visible category ticks — equals category count when interval={0}. */
  visibleTicksCount?: number;
  /** Raw Topic_Title of the open drawer — that label renders on a chip. */
  selectedCategory?: string | null;
}

/** Vertical drop from the axis line to the tick label's baseline. */
const TICK_BASELINE_DY = 16;

/**
 * Chip behind the selected topic's label — DESIGN.md § Chips & Badges `chip-slate`.
 * 6px radius, not a full pill: the system reserves pill shapes for status badges.
 */
const SELECTED_CHIP_PADDING_X = 8;
const SELECTED_CHIP_HEIGHT = 22;
const SELECTED_CHIP_RADIUS = 6;

/** Centres the chip on the label's x-height instead of its baseline. */
const SELECTED_CHIP_BASELINE_LIFT = 4.5;

/**
 * Horizontal Topic_Title tick. Truncates to the category band's pixel width so
 * neighboring labels never overlap; full name stays on hover/focus via <title>
 * and aria-label. Recharts' ResponsiveContainer re-renders ticks with a fresh
 * `width` on resize, so band width stays current without a local observer.
 */
export function CategoryTick({
  x = 0,
  y = 0,
  payload,
  width,
  visibleTicksCount = 1,
  selectedCategory,
}: AxisTickProps) {
  const raw = String(payload?.value ?? "");
  const full = formatTopicTitle(raw);
  const isSelected = selectedCategory != null && raw === selectedCategory;
  const categoryCount = Math.max(1, visibleTicksCount);
  const bandWidth =
    width != null && width > 0 ? width / categoryCount : Number.POSITIVE_INFINITY;
  // The chip's padding eats into the band, so the label has to fit inside it —
  // otherwise a selected long name would push its chip over its neighbours.
  const chipInset = isSelected ? SELECTED_CHIP_PADDING_X * 2 : 0;
  const availableWidth = bandWidth - LABEL_BAND_GUTTER - chipInset;
  const display = truncateLabelToWidth(full, availableWidth);
  const isTruncated = display !== full;
  const chipWidth = measureAxisLabelWidth(display) + SELECTED_CHIP_PADDING_X * 2;

  return (
    <g transform={`translate(${x},${y})`}>
      {isSelected && (
        <rect
          x={-chipWidth / 2}
          y={TICK_BASELINE_DY - SELECTED_CHIP_BASELINE_LIFT - SELECTED_CHIP_HEIGHT / 2}
          width={chipWidth}
          height={SELECTED_CHIP_HEIGHT}
          rx={SELECTED_CHIP_RADIUS}
          fill="var(--slate-chip-bg)"
        />
      )}
      <text
        data-testid="overview-chart-tick"
        textAnchor="middle"
        dy={TICK_BASELINE_DY}
        fontSize={CHART_TICK_FONT_SIZE}
        fontWeight={CHART_LABEL_FONT_WEIGHT}
        fill={isSelected ? "var(--slate-chip-text)" : CHART_AXIS_TEXT}
        aria-label={isTruncated ? full : undefined}
        tabIndex={isTruncated ? 0 : undefined}
      >
        {isTruncated && <title>{full}</title>}
        {display}
      </text>
    </g>
  );
}

/** Headroom above the plot area so the tallest bar's value label is not clipped. */
export const CHART_TOP_MARGIN = 36;

/**
 * The drawer Info-tab badge for one chart metric, matched by KPI display name and
 * topic. Returns null when this metric has no KPI series (e.g. chart-only metrics
 * like "Creatives %") or the topic is missing from it.
 */
function resolveMetricBadge(
  metric: ResolvedMetric,
  datum: ChartDatum,
  kpiSeries: OverviewKpiSeries[],
): KpiBadge | null {
  const series = kpiSeries.find((s) => s.kpi === metric.label);
  if (!series) return null;
  const point = series.data.find((p) => p.Topic_Title === datum.category);
  if (!point) return null;
  return resolveKpiBadge(metric.label, point, series);
}

/** Card-styled tooltip: drawer-style % badge + raw value per metric. */
export function ChartTooltip({
  active,
  payload,
  label,
  metrics,
  currencySymbol,
  kpiSeries,
}: TooltipContentProps<number, string> & {
  metrics: ResolvedMetric[];
  currencySymbol: string;
  kpiSeries: OverviewKpiSeries[];
}) {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  const datum = payload[0]?.payload as ChartDatum | undefined;
  if (!datum) return null;
  return (
    <div className="rounded-lg border bg-card p-3 text-xs shadow-md">
      <p className="mb-2 text-muted-foreground">
        {label != null ? formatTopicTitle(String(label)) : ""}
      </p>
      <p className="mb-2 text-[10px] leading-tight text-muted-foreground">
        {t("overview.chart.tooltipFootnote")}
      </p>
      {metrics.map((m) => {
        const badge = resolveMetricBadge(m, datum, kpiSeries);
        const raw = datum[rawMetricKey(m.id)];
        return (
          <div key={m.id} className="flex items-center gap-2">
            <span
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ background: m.colorVar }}
            />
            <span className="text-muted-foreground">{m.label}</span>
            {badge?.badgeText && (
              <span className={`font-medium tabular-nums ${KPI_TONE_CLASS[badge.tone]}`}>
                {badge.badgeText}
              </span>
            )}
            <span className="font-medium tabular-nums text-foreground">
              {formatRawMetric(m, typeof raw === "number" ? raw : null, currencySymbol)}
            </span>
          </div>
        );
      })}
    </div>
  );
}
